import { useState, useRef, useEffect } from "react";
import EditIcon from "@mui/icons-material/Edit";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import DeleteIcon from "@mui/icons-material/Delete";
import { Modal, Box, IconButton, Tooltip } from "@mui/material";
import "./UserProfile.css";
import InputField from "../../../../components/InputField";
import Button from "../../../../components/Button";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../../../context/AuthContext";
import api from "../../../../api/api";
import Breadcrumbs from "../../../../components/Breadcrumbs";
import ChangePasswordModal from "./ChangePasswordModal";
import LockIcon from '@mui/icons-material/Lock';
import SupervisorAccountIcon from "@mui/icons-material/SupervisorAccount";
import PersonIcon from '@mui/icons-material/Person';

const UserProfile = () => {
  const { user, setUser } = useAuth();
  const [formData, setFormData] = useState({
    companyName: user?.company_name || "",
    userName: user?.full_name || "",
    userNumber: user?.phone_number || "",
    userEmail: user?.email || "",
    gstNumber: user?.gst_number || "",
    rmName: user?.rm_name || "",
    rmMobile: user?.rm_mobile || "",
    rmEmail: user?.rm_email || "",
    profile_status: user?.profile_status || "",
  });
  const [openPasswordModal, setOpenPasswordModal] = useState(false);
  const [openGSTModal, setOpenGSTModal] = useState(false);
  const [tempGST, setTempGST] = useState("");
  const [selectedFile, setSelectedFile] = useState(null);
  const [gstModalError, setGstModalError] = useState("");
  const [showSubmitModal, setShowSubmitModal] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [gstVerificationLoading, setGstVerificationLoading] = useState(false);
  const [submitModalMessage, setSubmitModalMessage] = useState("");
  const [submitModalSuccess, setSubmitModalSuccess] = useState(false);
  const [uploadedFiles, setUploadedFiles] = useState({
    gst: null,
    pan: null,
    kyc: null,
    cheque: null,
  });
  const [uploadErrors, setUploadErrors] = useState({
    gst: '',
    pan: '',
    kyc: '',
    cheque: '',
  });
  const [errors, setErrors] = useState({
    userNumber: '',
    userEmail: '',
    gstNumber: '',
    gstDoc: '',
    panDoc: '',
    kycDoc: '',
    chequeDoc: '',
  });
  const navigate = useNavigate();


  const fileInputRef = useRef(null);
  const gstFileRef = useRef(null);
  const panFileRef = useRef(null);
  const kycFileRef = useRef(null);
  const chequeFileRef = useRef(null);


  useEffect(() => {
    const fetchUserDetails = async () => {
      try {
        const token = localStorage.getItem("token");
        if (!token) {
          window.location.href = "/login";
          return;
        }

        const { data } = await api.get(`/user-details/${user?.user_id}`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        setFormData({
          companyName: data.user.company_name || user.company_name || "",
          userName: data.user.full_name || user.full_name || "",
          userNumber: data.user.phone_number || user.phone_number || "",
          userEmail: data.user.email || user.email || "",
          gstNumber: data.user.gst_number || user.gst_number || "",
          rmName: data.user.rm_name || user.rm_name || "",
          rmMobile: data.user.rm_mobile || user.rm_mobile || "",
          rmEmail: data.user.rm_email || user.rm_email || "",
        });
        setUser(data.user);
      } catch (err) {
        console.error("Failed to fetch user details:", err.response || err);
      }
    };

    if (user?.user_id) {
      fetchUserDetails();
    }
  }, [user?.user_id]);

  const validateField = (name, value) => {
    let error = '';
    switch (name) {
      case 'userNumber':
        if (!value) {
          error = 'Mobile number is required';
        } else if (!/^\d{10}$/.test(value)) {
          error = 'Mobile number must be 10 digits';
        }
        break;
      case 'userEmail':
        if (!value) {
          error = 'Email is required';
        } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
          error = 'Invalid email format';
        }
        break;
      case 'gstNumber':
        if (user?.profile_status === "Unverified" || user?.profile_status === "VerificationRejected") {
          if (!value) {
            error = 'GST number is required';
          } else if (!/^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/.test(value)) {
            error = 'Invalid GST number format';
          }
        }
        break;
      default:
        break;
    }
    return error;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
    const error = validateField(name, value);
    setErrors({ ...errors, [name]: error });
  };

  const handleUpdate = async (e) => {
    e.preventDefault();

    // Validate fields
    const newErrors = {};
    Object.keys(formData).forEach(key => {
      const error = validateField(key, formData[key]);
      if (error) newErrors[key] = error;
    });

    // Validate documents if unverified or rejected
    if (user?.profile_status === "Unverified" || user?.profile_status === "VerificationPending" || user?.profile_status === "VerificationRejected") {
      const requiredDocs = ['gst', 'pan', 'kyc', 'cheque'];
      requiredDocs.forEach(doc => {
        if (!uploadedFiles[doc]) {
          newErrors[`${doc}Doc`] = `${doc.toUpperCase()} Document is required`;
        }
      });
    }

    setErrors(newErrors);

    if (Object.keys(newErrors).length > 0) {
      return;
    }

    if (user?.profile_status === "Unverified" || user?.profile_status === "VerificationPending" || user?.profile_status === "VerificationRejected") {
      setShowSubmitModal(true);
    } else {
      // For verified users, update phone_number, email, and gst_number
      try {
        const token = localStorage.getItem("token");
        const response = await api.post('/update-user-details', {
          user_id: user?.user_id,
          phone_number: formData.userNumber,
          email: formData.userEmail,
          gst_number: formData.gstNumber,
        }, {
          headers: { Authorization: `Bearer ${token}` },
        });
        console.log("User details updated successfully:", response.data);

        // Refresh the middleware token by fetching updated user details
        const { data: updatedUserData } = await api.get(`/user-details/${user?.user_id}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        // Update localStorage and context with new user data
        localStorage.setItem("user", JSON.stringify(updatedUserData.user));
        setUser(updatedUserData.user);

        alert("Profile updated successfully!");
        navigate("/");
      } catch (error) {
        console.error("Error updating user details:", error);
        alert("Failed to update profile. Please try again.");
      }
    }
  };

  const validateGSTFormat = (gst) => {
    // Basic GST format validation: 15 characters, specific pattern
    const gstRegex = /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/;
    return gstRegex.test(gst);
  };

  const handleConfirmSubmit = async () => {
    setIsSubmitting(true);
    setGstVerificationLoading(true);

    // Client-side GST validation before API call
    if (formData.gstNumber && !validateGSTFormat(formData.gstNumber.trim())) {
      setIsSubmitting(false);
      setGstVerificationLoading(false);
      setSubmitModalMessage("Invalid GST number format. Please check and try again.");
      setSubmitModalSuccess(false);
      return;
    }

    // Prepare FormData for API call
    const formDataToSend = new FormData();
    formDataToSend.append('user_id', user?.user_id);
    formDataToSend.append('company_name', formData.companyName);
    formDataToSend.append('full_name', formData.userName);
    formDataToSend.append('phone_number', formData.userNumber);
    formDataToSend.append('email', formData.userEmail);
    formDataToSend.append('gst_number', formData.gstNumber);
    formDataToSend.append('rm_name', formData.rmName);
    formDataToSend.append('rm_mobile', formData.rmMobile);
    formDataToSend.append('rm_email', formData.rmEmail);

    // Append files
    if (uploadedFiles.gst) formDataToSend.append('GST', uploadedFiles.gst);
    if (uploadedFiles.pan) formDataToSend.append('PAN', uploadedFiles.pan);
    if (uploadedFiles.kyc) formDataToSend.append('KYC', uploadedFiles.kyc);
    if (uploadedFiles.cheque) formDataToSend.append('Cheque', uploadedFiles.cheque);


    try {
      const response = await api.post('/upload-verification-documents', formDataToSend, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      console.log("Documents uploaded successfully:", response.data);

      // Refresh the middleware token by fetching updated user details
      const token = localStorage.getItem("token");
      if (token) {
        const { data: updatedUserData } = await api.get(`/user-details/${user?.user_id}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        // Update localStorage and context with new user data
        localStorage.setItem("user", JSON.stringify(updatedUserData.user));
        setUser(updatedUserData.user); // Assuming setUser is available from useAuth
      }

      setIsSubmitting(false);
      setGstVerificationLoading(false);
      setSubmitModalMessage("Verification documents submitted successfully!");
      setSubmitModalSuccess(true);
    } catch (error) {
      console.error("Error uploading documents:", error);
      console.error("Error response data:", error.response?.data); // <-- ADD THIS
      setIsSubmitting(false);
      setGstVerificationLoading(false);

      // Handle GST verification errors specifically
      if (error.response?.data?.error && error.response.data.error.includes("GST verification failed")) {
        setSubmitModalMessage(`GST Verification Failed: ${error.response.data.gst_verification?.message || "Invalid GST number"}`);
      } else {
        setSubmitModalMessage(error.response?.data?.error || "Failed to upload documents. Please try again.");
      }
      setSubmitModalSuccess(false);
    }
  };

  const openModal = () => {
    setTempGST(formData.gstNumber);
    setSelectedFile(null);
    setGstModalError("");
    setOpenGSTModal(true);
  };

  const handleSaveGST = async () => {
    if (!selectedFile) {
      setGstModalError("Please upload the updated document.");
      return;
    }

    if (!/^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/.test(tempGST)) {
      setGstModalError("Invalid GST number format.");
      return;
    }

    // Prepare FormData for API call
    const formDataToSend = new FormData();
    formDataToSend.append('user_id', user?.user_id);
    formDataToSend.append('updatedGstNo', tempGST);
    formDataToSend.append('gst', selectedFile);

    try {
      const response = await api.post('/update-gst-number', formDataToSend, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      console.log("GST update successful:", response.data);

      // Update formData and user context
      setFormData({ ...formData, gstNumber: tempGST });
      setUser({ ...user, profile_status: 'GSTUpdatePending' });

      // Refresh the middleware token by fetching updated user details
      const token = localStorage.getItem("token");
      if (token) {
        const { data: updatedUserData } = await api.get(`/user-details/${user?.user_id}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        // Update localStorage and context with new user data
        localStorage.setItem("user", JSON.stringify(updatedUserData.user));
        setUser(updatedUserData.user);
      }

      setOpenGSTModal(false);
      setShowSuccessModal(true);
    } catch (error) {
      console.error("Error updating GST:", error);
      setGstModalError(error.response?.data?.error || "Failed to update GST. Please try again.");
    }
  };

  const handleFileClick = () => fileInputRef.current.click();
  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.type !== 'application/pdf') {
        setGstModalError('Only PDF files are supported.');
        setSelectedFile(null);
      } else {
        setSelectedFile(file);
        setGstModalError("");
      }
    }
  };

  const handleDocumentUpload = (type, e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.type !== 'application/pdf') {
        setUploadErrors({ ...uploadErrors, [type]: 'Only PDF files are supported.' });
        setUploadedFiles({ ...uploadedFiles, [type]: null });
      } else {
        setUploadedFiles({ ...uploadedFiles, [type]: file });
        setUploadErrors({ ...uploadErrors, [type]: '' });
      }
    }
  };

  const handleDocumentClick = (type) => {
    switch (type) {
      case 'gst':
        gstFileRef.current.click();
        break;
      case 'pan':
        panFileRef.current.click();
        break;
      case 'kyc':
        kycFileRef.current.click();
        break;
      case 'cheque':
        chequeFileRef.current.click();
        break;
      default:
        break;
    }
  };

  const handleOpenFile = (type) => {
    const file = uploadedFiles[type];
    if (file) {
      const fileUrl = URL.createObjectURL(file);
      window.open(fileUrl, '_blank');
    }
  };

  const handleDeleteFile = (type) => {
    setUploadedFiles({ ...uploadedFiles, [type]: null });
    setUploadErrors({ ...uploadErrors, [type]: '' });
  };

  return (
    <>
    <Breadcrumbs />
      {/* <h2 className="page-title">User Profile</h2> */}
      <form onSubmit={handleUpdate}>

          <fieldset className="security-fieldset mt-4">
 

      <legend className="d-flex align-items-center">
    <PersonIcon fontSize="small" className="me-2 text-black" />
  Profile Information
  </legend>
        <div className="row g-3">
          {/* Company Name */}
          <div className="col-sm-6 col-md-4">

            <label className="form-label">Company Name</label>
            <InputField
              type="text"
              className="form-control"
              name="companyName"
              value={formData.companyName}
              onChange={handleChange}
              placeholder="Enter company name"
               readOnly
            />
          </div>

          {/* User Name */}
          <div className="col-sm-6 col-md-4">
            <label className="form-label">User Name</label>
            <InputField
              type="text"
              className="form-control"
              name="userName"
              value={formData.userName}
              onChange={handleChange}
              placeholder="Enter your name"
               readOnly
            />
          </div>

          {/* Mobile Number */}
          <div className="col-sm-6 col-md-4">
            <label className="form-label">Mobile Number</label>
            <InputField
              type="tel"
              className="form-control"
              name="userNumber"
              value={formData.userNumber}
              onChange={handleChange}
              placeholder="Enter mobile number"
              required
            />
            {errors.userNumber && <div className="text-danger mt-1">{errors.userNumber}</div>}
          </div>

          {/* Email */}
          <div className="col-sm-6 col-md-4">
            <label className="form-label">Email Address</label>
            <InputField
              type="email"
              className="form-control"
              name="userEmail"
              value={formData.userEmail}
              onChange={handleChange}
              placeholder="Enter email address"
              required
            />
            {errors.userEmail && <div className="text-danger mt-1">{errors.userEmail}</div>}
          </div>

          {/* GST Number */}
          {user?.profile_status === "Unverified" || user?.profile_status === "VerificationRejected" ? (
            <div className="col-sm-6 col-md-4">
              <label className="form-label">GST Number</label>
              <InputField
                type="text"
                className="form-control"
                name="gstNumber"
                value={formData.gstNumber}
                onChange={handleChange}
                placeholder="Enter GST number"
                required
              />
              {errors.gstNumber && <div className="text-danger mt-1">{errors.gstNumber}</div>}
            </div>
          ) : (
           
            <div className="col-sm-6 col-md-4 gst-field">
              <label className="form-label">GST Number</label>
              <div className="gst-InputField-wrapper">
                <InputField
                  type="text"
                  className="form-control gst-InputField"
                  name="gstNumber"
                  value={formData.gstNumber}
                  onChange={handleChange}
                  placeholder="Enter GST number"
                  readOnly
                />
                <EditIcon className="edit-icon" onClick={openModal} />
              </div>
            </div>
          )}
        </div>
        </fieldset>

        {(user?.profile_status === "Unverified" || user?.profile_status === "VerificationPending" || user?.profile_status === "VerificationRejected") && (
          <div className="mt-5">
            <h4 className="mb-4">Document Upload</h4>
            <p className="text-muted mb-4">Only PDF files are supported for upload.</p>
            <div className="row g-3">
              {/* GST Document */}
              <div className="col-sm-6 col-md-3">
                <label className="form-label">GST Document</label>
                <div className="document-upload-wrapper">
                  <Button
                    type="button"
                    variant="outline"
                    btncls="w-100 document-upload-btn"
                    onClick={() => handleDocumentClick('gst')}
                  >
                    <UploadFileIcon className="me-2" />
                    Upload GST
                  </Button>
                  <input
                    type="file"
                    ref={gstFileRef}
                    className="hidden-file-input"
                    onChange={(e) => handleDocumentUpload('gst', e)}
                    accept=".pdf"
                  />
                  {uploadedFiles.gst && (
                    <div className="d-flex align-items-center gap-2 mt-2">
                      <span
                        className="text-success small cursor-pointer text-decoration-underline text-break"
                        onClick={() => handleOpenFile('gst')}
                        role="button"
                        tabIndex={0}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" || e.key === " ") {
                            handleOpenFile('gst');
                          }
                        }}
                      >
                        {uploadedFiles.gst.name}
                      </span>
                      <DeleteIcon
                        style={{ cursor: "pointer" }}
                        onClick={() => handleDeleteFile('gst')}
                      />
                    </div>
                  )}
                  {errors.gstDoc && <div className="text-danger mt-1">{errors.gstDoc}</div>}
                </div>
              </div>

              {/* PAN Document */}
              <div className="col-sm-6 col-md-3">
                <label className="form-label">PAN Document</label>
                <div className="document-upload-wrapper">
                  <Button
                    type="button"
                    variant="outline"
                    btncls="w-100 document-upload-btn"
                    onClick={() => handleDocumentClick('pan')}
                  >
                    <UploadFileIcon className="me-2" />
                    Upload PAN
                  </Button>
                  <input
                    type="file"
                    ref={panFileRef}
                    className="hidden-file-input"
                    onChange={(e) => handleDocumentUpload('pan', e)}
                    accept=".pdf"
                  />
                  {uploadedFiles.pan && (
                    <div className="d-flex align-items-center gap-2 mt-2">
                      <span
                        className="text-success small cursor-pointer text-decoration-underline text-break"
                        onClick={() => handleOpenFile('pan')}
                        role="button"
                        tabIndex={0}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" || e.key === " ") {
                            handleOpenFile('pan');
                          }
                        }}
                      >
                        {uploadedFiles.pan.name}
                      </span>
                      <DeleteIcon
                        style={{ cursor: "pointer" }}
                        onClick={() => handleDeleteFile('pan')}
                      />
                    </div>
                  )}
                  {errors.panDoc && <div className="text-danger mt-1">{errors.panDoc}</div>}
                </div>
              </div>

              {/* KYC Document */}
              <div className="col-sm-6 col-md-3">
                <label className="form-label">KYC Document</label>
                <div className="document-upload-wrapper">
                  <Button
                    type="button"
                    variant="outline"
                    btncls="w-100 document-upload-btn"
                    onClick={() => handleDocumentClick('kyc')}
                  >
                    <UploadFileIcon className="me-2" />
                    Upload KYC
                  </Button>
                  <input
                    type="file"
                    ref={kycFileRef}
                    className="hidden-file-input"
                    onChange={(e) => handleDocumentUpload('kyc', e)}
                    accept=".pdf"
                  />
                  {uploadedFiles.kyc && (
                    <div className="d-flex align-items-center gap-2 mt-2">
                      <span
                        className="text-success small cursor-pointer text-decoration-underline text-break"
                        onClick={() => handleOpenFile('kyc')}
                        role="button"
                        tabIndex={0}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" || e.key === " ") {
                            handleOpenFile('kyc');
                          }
                        }}
                      >
                        {uploadedFiles.kyc.name}
                      </span>
                      <DeleteIcon
                        style={{ cursor: "pointer" }}
                        onClick={() => handleDeleteFile('kyc')}
                      />
                    </div>
                  )}
                  {errors.kycDoc && <div className="text-danger mt-1">{errors.kycDoc}</div>}
                </div>
              </div>

              {/* Cancelled Cheque */}
              <div className="col-sm-6 col-md-3">
                <label className="form-label">Cancelled Cheque</label>
                <div className="document-upload-wrapper">
                  <Button
                    type="button"
                    variant="outline"
                    btncls="w-100 document-upload-btn"
                    onClick={() => handleDocumentClick('cheque')}
                  >
                    <UploadFileIcon className="me-2" />
                    Upload Cheque
                  </Button>
                  <input
                    type="file"
                    ref={chequeFileRef}
                    className="hidden-file-input"
                    onChange={(e) => handleDocumentUpload('cheque', e)}
                    accept=".pdf"
                  />
                  {uploadedFiles.cheque && (
                    <div className="d-flex align-items-center gap-2 mt-2">
                      <span
                        className="text-success small cursor-pointer text-decoration-underline text-break"
                        onClick={() => handleOpenFile('cheque')}
                        role="button"
                        tabIndex={0}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" || e.key === " ") {
                            handleOpenFile('cheque');
                          }
                        }}
                      >
                        {uploadedFiles.cheque.name}
                      </span>
                      <DeleteIcon
                        style={{ cursor: "pointer" }}
                        onClick={() => handleDeleteFile('cheque')}
                      />
                    </div>
                  )}
                  {errors.chequeDoc && <div className="text-danger mt-1">{errors.chequeDoc}</div>}
                </div>
              </div>
            </div>
          </div>
        )}
        {/* RM Details */}
       {(user?.profile_status === "Verified" || user?.profile_status === "GSTUpdatePending" || user?.profile_status === "RequestRejected") && (

   

    <fieldset className="security-fieldset mt-4">
  <legend className="d-flex align-items-center">
    <SupervisorAccountIcon fontSize="small" className="me-2 text-black" />
    Relationship Manager
  </legend>

  <div className="row g-3">
    <div className="col-sm-6 col-md-4">
        <label className="form-label">Relationship Manager Name</label>
        <InputField
          type="text"
          className="form-control"
          name="rmName"
          value={formData.rmName}
          onChange={handleChange}
          placeholder="Enter RM name"
          readOnly
        />
      </div>

      <div className="col-sm-6 col-md-4">
        <label className="form-label">Relationship Manager Email</label>
        <InputField
          type="email"
          className="form-control"
          name="rmEmail"
          value={formData.rmEmail}
          onChange={handleChange}
          placeholder="Enter RM email address"
          readOnly
        />
      </div>

      <div className="col-sm-6 col-md-4">
        <label className="form-label">Relationship Manager Number</label>
        <InputField
          type="tel"
          className="form-control"
          name="rmMobile"
          value={formData.rmMobile}
          onChange={handleChange}
          placeholder="Enter RM mobile number"
          readOnly
        />
      </div>
  </div>
</fieldset>
)}
       
          {/* Security */}


  <fieldset className="security-fieldset">
  <legend>
    <LockIcon className="me-2" />
    Security
  </legend>

  <div className="mt-3">
    <button
      className="btn btn-primary"
      onClick={() => setOpenPasswordModal(true)}
    >
      Change Password
    </button>
  </div>
</fieldset>
       

        <div className="mt-4 row justify-content-end">
          <div className="col-md-3">
            {(user?.profile_status === "Unverified" || user?.profile_status === "VerificationPending" || user?.profile_status === "VerificationRejected") && (
              <Button
                type="submit"
                variant="primary"
                btncls="w-100 w-md-auto profilebtn"
              >
                Submit
              </Button>
            )}
            {(user?.profile_status === "Verified" || user?.profile_status === "GSTUpdatePending" || user?.profile_status === "RequestRejected") && (
              <div className="d-flex justify-content-end">
              <Button
                type="submit"
                variant="primary"
                btncls="w-95 w-md-auto profilebtn"
               
              >
                Update
              </Button>
              </div>
            )}
          </div>
        </div>
      </form>

      <Modal open={openGSTModal} onClose={() => setOpenGSTModal(false)}>
        <Box
          className="modal-box"
          style={{ height: selectedFile ? "305px" : "280px" }}
        >
          <h6 className="fw-bold">Edit GST Number</h6>
          <p>Enter your GST number and upload the updated document</p>

          <div className="position-relative">
            <InputField
              label="GST Number"
              className="form-control"
              value={tempGST}
              onChange={(e) => setTempGST(e.target.value)}
            />
            <Tooltip title="Upload GST Certificate">
              <IconButton
                color="primary"
                onClick={handleFileClick}
                className="fileuploadicon position-absolute"
              >
                <UploadFileIcon />
              </IconButton>
            </Tooltip>
            <input
              type="file"
              ref={fileInputRef}
              className="hidden-file-input"
              placeholder="Enter Your GST Number"
              onChange={handleFileChange}
              accept=".pdf"
            />
          </div>

          {gstModalError && <div className="text-danger mt-2">{gstModalError}</div>}

          {selectedFile && (
            <h6 className="file-name">
              <span className="fw-bold">Selected file: </span>
              <span
                style={{ cursor: 'pointer', color: 'blue', textDecoration: 'underline' }}
                onClick={() => window.open(URL.createObjectURL(selectedFile), '_blank')}
              >
                {selectedFile.name}
              </span>
            </h6>
          )}

          <div className="row mt-5 justify-content-center">
            <div className="col-6">
              <Button
                variant="secondary"
                btncls="w-100"
                onClick={() => setOpenGSTModal(false)}
              >
                Cancel
              </Button>
            </div>
            <div className="col-6">
              <Button variant="primary" btncls="w-100" onClick={handleSaveGST}>
                Submit
              </Button>
            </div>
          </div>
        </Box>
      </Modal>

      <Modal open={showSubmitModal} onClose={() => setShowSubmitModal(false)}>
        <Box className="modal-box" style={{ height: "200px" }}>
          <h6 className="fw-bold">Confirm Submission</h6>
          <p>Are you sure you want to submit your verification documents?</p>

          <div className="row mt-5 justify-content-center">
            <div className="col-6">
              <Button
                variant="secondary"
                btncls="w-100"
                onClick={() => setShowSubmitModal(false)}
              >
                Cancel
              </Button>
            </div>
            <div className="col-6">
              <Button variant="primary" btncls="w-100" onClick={handleConfirmSubmit} disabled={isSubmitting}>
                {gstVerificationLoading ? "Verifying GST..." : isSubmitting ? "Submitting..." : "OK"}
              </Button>
            </div>
          </div>
        </Box>
      </Modal>

      <Modal open={submitModalMessage !== ""} onClose={() => setSubmitModalMessage("")}>
        <Box className="modal-box" style={{ height: "200px" }}>
          <h6 className="fw-bold">{submitModalSuccess ? "Success" : "Error"}</h6>
          <p>{submitModalMessage}</p>

          <div className="row mt-5 justify-content-center">
            <div className="col-6">
              <Button variant="primary" btncls="w-100" onClick={() => {
                setSubmitModalMessage("");
                if (submitModalSuccess) {
                  navigate("/");
                }
              }}>
                OK
              </Button>
            </div>
          </div>
        </Box>
      </Modal>

      <Modal open={showSuccessModal} onClose={() => setShowSuccessModal(false)}>
        <Box className="modal-box" style={{ height: "200px" }}>
          <h6 className="fw-bold">Request Sent</h6>
          <p>Your request for update GST details has been sent successfully.</p>

          <div className="row mt-5 justify-content-center">
            <div className="col-6">
              <Button variant="primary" btncls="w-100" onClick={() => {
                setShowSuccessModal(false);
                navigate("/");
              }}>
                OK
              </Button>
            </div>
          </div>
        </Box>
      </Modal>
      <ChangePasswordModal
  open={openPasswordModal}
  handleClose={() => setOpenPasswordModal(false)}
  user={user}
  />
    </>
  );
};

export default UserProfile;